import as from 'ansis'
import semver from 'semver'
import { z } from 'zod'

import {
  version as currentVersion,
  name as packageName,
} from '@/../package.json'
import { getMetadata, setMetadata } from '@/helpers/metadata'
import { output } from '@/helpers/std-out'

const packageJsonSchema = z.object({
  version: z.string(),
})

/**
 * Fetches package information from the npm registry
 * @param packageName - The name of the npm package to fetch
 * @param version - The version to fetch (defaults to 'latest')
 * @returns The parsed package.json data for the specified version
 * @example
 * // Get latest version info for 'lodash'
 * const pkg = await getRemotePackageJson('lodash')
 * // Get specific version info
 * const pkg = await getRemotePackageJson('lodash', '4.17.21')
 */
export async function getRemotePackageJson(
  packageName: string,
  version = 'latest',
) {
  const response = await fetch(
    `https://registry.npmjs.org/${packageName}/${version}`,
  )

  const body = await response.json()

  return packageJsonSchema.parse(body)
}

/**
 * Checks if there is a newer version of the package available
 * @returns Object containing upgrade information including current and latest versions
 * @example
 * // Check if upgrade is available
 * const info = await getUpgradeInformation()
 * if (info.upgrade) {
 *   console.log(`Upgrade available from ${info.currentVersion} to ${info.latestVersion}`)
 * }
 */
export async function getUpgradeInformation() {
  const { version: latestVersion } = await getRemotePackageJson(packageName)

  if (semver.gte(currentVersion, latestVersion)) {
    return {
      upgrade: false as const,
    }
  }

  return {
    upgrade: true as const,
    name: packageName,
    currentVersion,
    latestVersion,
  }
}

/**
 * Checks for available upgrades periodically and notifies the user if a new version is available
 * Only checks once per hour to avoid excessive API calls
 * @returns Promise that resolves when the check is complete
 * @example
 * // Check for upgrades and notify user if available
 * await checkUpgrade()
 * // Will only check once per hour, subsequent calls within that period will be skipped
 */
export async function checkUpgrade() {
  const emitOutput = () =>
    output
      .info()
      .message(
        `New version available! Run ${as.cyan('`scalar upgrade`')} to install the latest version`,
      )
      .print()

  const now = performance.now()

  const delay = 1 * 60 * 60 * 1000 // 1 hr in ms
  const {
    lastUpdateCheck = now - delay - 1,
    lastKnownVersion = currentVersion,
  } = await getMetadata()

  if (lastUpdateCheck + delay < now) {
    return getUpgradeInformation()
      .then(async ({ upgrade, latestVersion }) => {
        await setMetadata({
          lastUpdateCheck: now,
          lastKnownVersion: latestVersion,
        })

        if (upgrade) {
          emitOutput()
        }
      })
      .catch()
  }

  if (semver.lt(currentVersion, lastKnownVersion)) {
    emitOutput()
  }
}
